/**
 * AgentWorkspaceBrowser — VSCode-like, read-only view of an agent's persistent
 * workspace ("the desk"), as of its last run. LAZY: lists ONE folder at a time
 * from S3 (never downloads the whole tree), so it scales to any repo size.
 *
 *   • a column file/folder list — click a folder to descend, breadcrumb to go up
 *   • multi-format VIEWER (view-only), routed by extension:
 *       - code / text   → highlight.js (190+ langs)         [lazy-loaded]
 *       - .md            → MarkdownRenderer (react-markdown)
 *       - pdf/docx/xlsx
 *         /pptx/csv/img  → @cyntler/react-doc-viewer (native, presigned URL) [lazy]
 *       - else           → download prompt
 *   • per-file DOWNLOAD (presigned ?download=1) + whole-workspace .zip download
 *
 * Honest scope: this is the LAST SAVED snapshot (the builder is ephemeral), not
 * a live IDE — labeled "as of <savedAt>". No editing.
 *
 * Props: { networkId, poolId }
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronRight, File as FileIcon, Folder, Download, Home, Trash2,
} from 'lucide-react';
import {
  getAgentWorkspaceTree,
  getAgentWorkspaceFile,
  getAgentWorkspaceRawUrl,
  getAgentWorkspaceDownloadUrl,
  deleteAgentWorkspaceFile,
  listWorkspaces,
} from '../../services/toolsApi';
import { listAgentConnectorOverrides } from '../../services/poolMembersApi';
import MarkdownRenderer from '../MarkdownRenderer.jsx';
import Spinner from '../Spinner.jsx';

// Sentinel "agent" option that shows the SHARED room workspace (the whole
// /workspace/repo: manuscript + the build/ deliverable) instead of one agent's
// per-agent desk. Resolves to the pool's workspace via ``listWorkspaces``.
const ROOM_WORKSPACE_OPTION = '__room__';

const LazyDocView = lazy(() => import('./WorkspaceDocView.jsx'));
const LazyCodeView = lazy(() => import('./WorkspaceCodeView.jsx'));

function _fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function _ext(path) {
  const base = String(path || '').split('/').pop() || '';
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot).toLowerCase() : '';
}

const DOC_EXTS = new Set([
  '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt', '.csv',
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.tiff', '.tif',
]);
const MD_EXTS = new Set(['.md', '.markdown']);

function classifyFile(path, contentType) {
  const ext = _ext(path);
  if (MD_EXTS.has(ext)) return 'markdown';
  if (DOC_EXTS.has(ext)) return 'doc';
  if (String(contentType || '').startsWith('image/')) return 'doc';
  return 'code';
}

export default function AgentWorkspaceBrowser({ networkId, poolId }) {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(ROOM_WORKSPACE_OPTION);   // default: shared room workspace
  const [folder, setFolder] = useState(null); // { savedAt, kind, prefix, breadcrumb, entries, cold? }
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false); // a fetch has COMPLETED for the current selection
  const [error, setError] = useState('');
  const [prefix, setPrefix] = useState(''); // current folder path
  const [selected, setSelected] = useState(null); // { path, size, contentType }
  const [view, setView] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // The pool's SHARED room workspace id (for the "Room workspace" option). The
  // room runtime pins ONE pool-scoped workspace (the bootstrapped one); list it
  // and take the first POOL-scoped entry. '' when none → option hidden.
  const [roomWorkspaceId, setRoomWorkspaceId] = useState('');

  // Roster of builders in this room + the shared room workspace.
  useEffect(() => {
    let cancelled = false;
    if (!networkId || !poolId) return undefined;
    listAgentConnectorOverrides(networkId, poolId)
      .then((res) => {
        if (cancelled) return;
        const list = (Array.isArray(res?.agents) ? res.agents : [])
          .map((a) => ({ agentId: a.agentId, agentLabel: a.agentLabel || a.agentId }))
          .filter((a) => a.agentId);
        setAgents(list);
        // Default to the shared ROOM workspace (not the first agent) — it's the common landing
        // view (what the room produced overall), and matches the "⚙ Shared"-first convention used
        // on the workbench Agents tab. The user picks a specific agent's workspace explicitly.
        setAgentId((cur) => cur || ROOM_WORKSPACE_OPTION);
      })
      .catch(() => { if (!cancelled) setAgents([]); });
    // Resolve the shared room workspace (POOL-scoped) for the "Room workspace" option.
    listWorkspaces({ networkId, poolId })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.secrets) ? res.secrets : [];
        const poolWs = rows.find((r) => (r?.scope?.kind || '').toUpperCase() === 'POOL'
          && (r?.scope?.poolId || '') === poolId);
        setRoomWorkspaceId(poolWs?.secretId || '');
      })
      .catch(() => { if (!cancelled) setRoomWorkspaceId(''); });
    return () => { cancelled = true; };
  }, [networkId, poolId]);

  const isRoomView = agentId === ROOM_WORKSPACE_OPTION;
  // ``workspaceId`` is sent only for the room view → backend reads the shared
  // content tree; otherwise '' → per-agent desk (unchanged). The agent-path
  // segment is still required by the route, so pass a stable placeholder.
  const treeAgentSeg = isRoomView ? '_room_' : agentId;
  const treeOpts = useMemo(
    () => (isRoomView && roomWorkspaceId ? { workspaceId: roomWorkspaceId } : {}),
    [isRoomView, roomWorkspaceId],
  );

  // Load ONE folder (lazy). Called on agent change, prefix change, navigate.
  const loadFolder = useCallback(async (pfx) => {
    if (!networkId || !agentId) return;
    if (isRoomView && !roomWorkspaceId) {
      setFolder(null); setLoaded(true);
      setError('No shared room workspace exists for this room yet.');
      return;
    }
    // Spin while the API call is in flight. ``loaded`` stays false so the
    // empty / cold messages can NEVER render before a fetch completes (the bug:
    // a null ``folder`` pre-fetch satisfied ``isEmptyRoot`` and flashed
    // "This workspace is empty." before the real tree arrived).
    setLoading(true); setError('');
    try {
      const f = await getAgentWorkspaceTree(networkId, treeAgentSeg, pfx, treeOpts);
      setFolder(f);
    } catch (e) {
      setError(e?.message || 'Could not load workspace');
      setFolder(null);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [networkId, agentId, isRoomView, roomWorkspaceId, treeAgentSeg, treeOpts]);

  // Reset to root + clear the loaded flag when the selection changes, so the
  // spinner (not a stale tree or the empty message) shows during the new fetch.
  useEffect(() => { setPrefix(''); setSelected(null); setView(null); setLoaded(false); }, [agentId]);
  useEffect(() => { setLoaded(false); loadFolder(prefix); }, [loadFolder, prefix]);

  const openFolder = useCallback((path) => {
    setSelected(null); setView(null); setPrefix(path);
  }, []);

  const openFile = useCallback(async (entry) => {
    setSelected(entry);
    const kind = classifyFile(entry.path, entry.contentType);
    setView({ loading: true, kind });
    try {
      if (kind === 'doc') {
        const { url } = await getAgentWorkspaceRawUrl(networkId, treeAgentSeg, entry.path, treeOpts);
        setView({ kind, url });
      } else {
        const f = await getAgentWorkspaceFile(networkId, treeAgentSeg, entry.path, treeOpts);
        if (f.binary) {
          const { url } = await getAgentWorkspaceRawUrl(networkId, treeAgentSeg, entry.path, treeOpts);
          setView({ kind: 'doc', url });
        } else {
          setView({ kind, content: f.content ?? '' });
        }
      }
    } catch (e) {
      setView({ kind, error: e?.message || 'Could not open file' });
    }
  }, [networkId, treeAgentSeg, treeOpts]);

  const downloadFile = useCallback(async (path) => {
    try {
      const { url } = await getAgentWorkspaceRawUrl(networkId, treeAgentSeg, path, { download: true, ...treeOpts });
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) { setError(e?.message || 'Download failed'); }
  }, [networkId, treeAgentSeg, treeOpts]);

  // On-demand delete of a user upload/output (doc/arch/ATTACHMENTS.md). Only
  // offered for paths under uploads/ or outputs/ (the user-facing artifact dirs);
  // code/tree files are managed by the agent, not hand-deleted here.
  const deleteEntry = useCallback(async (entry) => {
    const path = entry.isDir ? (entry.path.endsWith('/') ? entry.path : `${entry.path}/`) : entry.path;
    const what = entry.isDir ? `everything in "${entry.name}/"` : `"${entry.name}"`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${what}? This removes the file(s) from the workspace.`)) return;
    try {
      await deleteAgentWorkspaceFile(networkId, treeAgentSeg, path, treeOpts);
      if (selected?.path === entry.path) { setSelected(null); setView(null); }
      loadFolder(prefix);  // refresh the listing
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  }, [networkId, treeAgentSeg, treeOpts, selected, prefix, loadFolder]);

  const onDownloadAll = useCallback(async () => {
    setDownloading(true);
    try {
      const { url } = await getAgentWorkspaceDownloadUrl(networkId, treeAgentSeg, treeOpts);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) { setError(e?.message || 'Download failed'); }
    finally { setDownloading(false); }
  }, [networkId, treeAgentSeg, treeOpts]);

  const entries = useMemo(() => folder?.entries || [], [folder]);
  // Breadcrumb root carries the friendly workspace name (``{network}-{pool}``,
  // e.g. "peach-tahr-kai") from the desk pointer — a git-init workspace has no
  // project name, so this gives the root crumb a meaningful label instead of
  // the generic "workspace".
  const crumb = useMemo(() => {
    const base = folder?.breadcrumb || [{ name: 'workspace', path: '' }];
    const dn = String(folder?.displayName || '').trim();
    if (!dn) return base;
    return base.map((c) => (c.path === '' ? { ...c, name: dn } : c));
  }, [folder]);
  // ``busy`` = spin while loading OR before the first fetch has completed for
  // this selection. The empty / cold messages require ``loaded`` so they can't
  // render against a null/stale ``folder`` mid-load.
  const busy = loading || !loaded;
  const cold = loaded && !loading && !error && folder?.cold;
  const isEmptyRoot = loaded && !loading && !error && !cold && prefix === '' && entries.length === 0;
  const showDownloadAll = !busy && !cold && !error && folder && !isEmptyRoot;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="agent-workspace-browser">
      {/* Header: agent + savedAt + download-all */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2 text-[11px] text-gray-500">
          {(agents.length > 1 || roomWorkspaceId) ? (
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              data-testid="agent-workspace-agent-select"
              className="shrink-0 rounded-md border border-gray-300 px-1.5 py-1 text-xs font-medium text-gray-700"
            >
              {/* Shared room workspace (whole /workspace/repo) — the full tree
                  the agents collaborate in, incl. the build/ deliverable. */}
              {roomWorkspaceId ? (
                <option value={ROOM_WORKSPACE_OPTION}>Room workspace (shared)</option>
              ) : null}
              {agents.map((a) => <option key={a.agentId} value={a.agentId}>{a.agentLabel}</option>)}
            </select>
          ) : (
            <span className="font-medium text-gray-700">{agents[0]?.agentLabel || 'Workspace'}</span>
          )}
          {folder?.savedAt ? (
            <span className="min-w-0 truncate">
              as of last run ({String(folder.savedAt).replace('T', ' ').replace('Z', ' UTC')})
            </span>
          ) : null}
        </div>
        {showDownloadAll ? (
          <button
            type="button"
            onClick={onDownloadAll}
            disabled={downloading}
            data-testid="agent-workspace-download"
            className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {downloading ? 'Preparing…' : 'Download all (.zip)'}
          </button>
        ) : null}
      </div>

      {busy ? (
        <Spinner variant="panel" label="Loading workspace…" testId="agent-workspace-loading" />
      ) : error ? (
        <div className="p-4 text-xs text-red-600">
          {error}<button type="button" onClick={() => loadFolder(prefix)} className="ml-2 underline">Retry</button>
        </div>
      ) : cold ? (
        <div className="p-6 text-center text-xs text-gray-500">
          This agent has no saved workspace yet — it appears after the agent's first run.
        </div>
      ) : isEmptyRoot ? (
        <div className="p-6 text-center text-xs text-gray-500">This workspace is empty.</div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Left: breadcrumb + current-folder list */}
          <div className="flex w-1/3 min-w-[12rem] max-w-xs flex-col border-r border-gray-100">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-100 px-2 py-1.5 text-[11px] text-gray-500">
              {crumb.map((c, i) => (
                <React.Fragment key={c.path}>
                  {i > 0 ? <ChevronRight className="h-3 w-3 text-gray-300" /> : null}
                  <button
                    type="button"
                    onClick={() => openFolder(c.path)}
                    className={`flex items-center gap-0.5 rounded px-1 hover:bg-gray-100 ${
                      i === crumb.length - 1 ? 'font-medium text-gray-800' : 'text-blue-600'
                    }`}
                  >
                    {i === 0 ? <Home className="h-3 w-3" /> : null}
                    <span className="truncate max-w-[8rem]">{c.name}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
            {/* Folder contents */}
            <div className="min-h-0 flex-1 overflow-y-auto py-1">
              {entries.length === 0 ? (
                <div className="px-3 py-2 text-[11px] text-gray-400">Empty folder.</div>
              ) : entries.map((e) => {
                // Deletable = user-facing artifact dirs only (uploads/, outputs/).
                const p = String(e.path || '');
                const deletable = p.startsWith('uploads/') || p === 'uploads'
                  || p.startsWith('outputs/') || p === 'outputs';
                return (
                <div
                  key={e.path}
                  className={`group flex w-full items-center gap-1.5 px-3 py-1 text-[11px] ${
                    !e.isDir && selected?.path === e.path
                      ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-blue-50/40'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => (e.isDir ? openFolder(e.path) : openFile(e))}
                    data-testid={`agent-workspace-entry-${e.path}`}
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    title={e.path}
                  >
                    {e.isDir
                      ? <Folder className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                      : <FileIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                    <span className="truncate">{e.name}</span>
                  </button>
                  {e.common ? <span className="shrink-0 rounded bg-amber-50 px-1 text-[9px] text-amber-700">key</span>
                    : (!e.isDir ? <span className="shrink-0 text-[9px] text-gray-400">{_fmtBytes(e.size)}</span> : null)}
                  {deletable ? (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); deleteEntry(e); }}
                      data-testid={`agent-workspace-delete-${e.path}`}
                      title={e.isDir ? 'Delete this batch' : 'Delete this file'}
                      className="ml-1 shrink-0 rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-600 group-hover:text-gray-400"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
                );
              })}
            </div>
          </div>

          {/* Right: viewer */}
          <div className="flex min-w-0 flex-1 flex-col">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-xs text-gray-400">
                Select a file to view it.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-1.5">
                  <span className="truncate text-[11px] font-mono text-gray-600" title={selected.path}>
                    {selected.path} · {_fmtBytes(selected.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => downloadFile(selected.path)}
                    data-testid="agent-workspace-file-download"
                    className="flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="h-3 w-3" /> Download
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-auto">
                  <Suspense fallback={<Spinner label="Loading viewer…" className="p-4" />}>
                    {view?.loading ? (
                      <Spinner label={`Loading ${selected.path}…`} className="p-4" />
                    ) : view?.error ? (
                      <div className="p-4 text-xs text-red-600">{view.error}</div>
                    ) : view?.kind === 'markdown' ? (
                      <div className="px-4 py-3"><MarkdownRenderer content={view.content || ''} /></div>
                    ) : view?.kind === 'doc' && view?.url ? (
                      <LazyDocView url={view.url} fileName={selected.path.split('/').pop()} />
                    ) : view?.kind === 'code' ? (
                      <LazyCodeView content={view.content || ''} />
                    ) : (
                      <div className="p-6 text-center text-xs text-gray-500">
                        Can’t preview this file type. Use Download to export it.
                      </div>
                    )}
                  </Suspense>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
